import type React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import styled from "styled-components"
import { LanguageSelect } from "~/components/Select/LanguageSelect"
import { captureEvent } from "~/modules/analytics/capture"
import { clearAuthToken } from "~/modules/authentication"
import { useStore } from "~/store"
import { useTranslate } from "~/ur-react-hooks"
import { useMedia } from "~/utils/hooks/useMedia"
import { useOrganizationMembership } from "~/utils/hooks/useOrganizationMembership"
import { Icon } from "../Icon"
import { SidebarDropdown } from "./SidebarDropdown"
import { SidebarNavItem } from "./SidebarLinkItem"

interface WrapperProps {
  open?: boolean
}

const Wrapper = styled.div<WrapperProps>`
  position: fixed;
  top: ${(props) => props.theme.sizes.headerHeight};
  left: 0;

  display: ${(props) => (props.open ? "flex" : "none")};
  flex-direction: column;
  width: ${(props) => props.theme.sizes.sidebarWidth};

  background: ${(props) => props.theme.colors.primary99};
  justify-content: space-between;

  ${(props) => props.theme.media.desktop} {
    height: calc(100vh - ${(props) => props.theme.sizes.headerHeight});
  }

  ${(props) => props.theme.media.pad} {
    position: absolute;
    z-index: 2;
    top: ${(props) => props.theme.sizes.headerHeightMobile};

    flex: 1;
    width: 70%;
    height: calc(100vh - ${(props) => props.theme.sizes.headerHeight});
    background: white;
  }
`

const TopContent = styled.div`
  width: 100%;
  padding-top: 8px;
`

const BottomContent = styled.div`
  padding-bottom: 10px;
`

const CloseIconWrapper = styled.div`
  padding: 15px 20px;
  color: ${(props) => props.theme.colors.primary90};
`

const MobileCloseShadow = styled.div<WrapperProps>`
  top: 0;
  right: 0;

  display: ${(props) => (props.open ? "flex" : "none")};

  justify-content: end;

  background: ${(props) => props.theme.colors.primary};

  position: absolute;

  flex: 1;
  width: 30%;
  height: 100%;
  z-index: 3;
  opacity: 0.9;

  :hover {
    cursor: pointer;
  }
`
const SidebarLanguageSelectWrapper = styled.div`
  display: flex;
  padding-left: 3px;
`

export type SidebarProps = {}

export const Sidebar: React.CFC<SidebarProps> = () => {
  const translations = useTranslate({
    home: "common.home",
    mathematics: "common.mathematics",
    myWorksheets: "common.my-worksheets",
    newWorksheet: "common.new-worksheet",
    norwegian: "common.norwegian",
    english: "common.english",
    users: "common.users",
    subscription: "common.subscription",
    language: "common.language",
    settings: "common.settings",
    logOut: "common.log-out",
  })

  // const user = useUser()
  const navigate = useNavigate()
  const location = useLocation()

  /* TODO: Replace */
  const sidebarOpen = useStore((state) => state.sidebar.open)
  const setSidebarOpen = useStore((state) => state.setSidebarOpen)

  const { pad } = useMedia()
  const organizationMembership = useOrganizationMembership()

  const handleLogout = () => {
    clearAuthToken()
    captureEvent("logout")
    navigate("/login")
  }

  return (
    <>
      <Wrapper open={sidebarOpen || !pad}>
        <TopContent>
          <SidebarDropdown
            key={`generator-${location.pathname.startsWith("/generator")}`}
            icon="Document"
            iconWidth={"12px"}
            iconLeft={"2px"}
            text={translations.newWorksheet}
            defaultOpen={location.pathname.startsWith("/generator")}
          >
            <SidebarNavItem
              icon="Math"
              iconType="regular"
              to="/generator/mathematics"
              text={translations.mathematics}
              dropdownItem={true}
              onClick={() => {
                captureEvent("open_new_worksheet", {
                  type: "mathematics",
                  source: "sidebar",
                })
              }}
            />
            <SidebarNavItem
              icon="Books"
              iconType="regular"
              to="/generator/norwegian"
              text={translations.norwegian}
              dropdownItem={true}
              onClick={() => {
                captureEvent("open_new_worksheet", {
                  type: "norwegian",
                  source: "sidebar",
                })
              }}
            />
            <SidebarNavItem
              icon="Globe"
              iconType="regular"
              to="/generator/english"
              text={translations.english}
              dropdownItem={true}
              onClick={() => {
                captureEvent("open_new_worksheet", {
                  type: "english",
                  source: "sidebar",
                })
              }}
            />
          </SidebarDropdown>

          <SidebarNavItem
            icon="Home"
            iconType="regular"
            iconWidth="18px"
            iconLeft="-1px"
            to="/dashboard"
            text={translations.home}
          />
          <SidebarNavItem
            icon="Book"
            iconType="regular"
            to="/my-worksheets"
            text={translations.myWorksheets}
          />
          {organizationMembership && (
            <SidebarNavItem
              icon="User"
              iconType="regular"
              to="/users"
              text={translations.users}
            />
          )}
        </TopContent>

        <BottomContent>
          {pad && (
            <SidebarLanguageSelectWrapper>
              <LanguageSelect
                iconWidth="16px"
                fontSize="16px"
                fontWeight={400}
                height="48px"
                width="100%"
                noOptionsText={translations.language}
                curtainProps={{
                  extendLeft: "52px",
                  padding: "15px 0px 15px 75px",
                  inline: true,
                }}
              />
            </SidebarLanguageSelectWrapper>
          )}
          <SidebarNavItem
            icon="Cog"
            iconType="regular"
            to="/settings"
            text={translations.settings}
          />
          <SidebarNavItem
            icon="Exit"
            iconType="regular"
            to="/login"
            text={translations.logOut}
            onClick={handleLogout}
          />
        </BottomContent>
      </Wrapper>

      <MobileCloseShadow
        open={sidebarOpen && pad}
        onClick={() => setSidebarOpen(false)}
      >
        <CloseIconWrapper>
          <Icon icon="times-circle" size="24px" type="solid" />
        </CloseIconWrapper>
      </MobileCloseShadow>
    </>
  )
}
